import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, User } from "lucide-react";
import { VOCI_MENU } from "@/lib/menuLaterale";

// Sprint CRM-1 — elementi comuni delle pagine CRM (regole in /app/memory/crm/REGOLE_CRM.md)
// Sprint CRM-1.1 — «In corso» è l'etichetta dello stato «aperta» (il valore salvato non cambia), ovunque
export const STATI_OPP = { aperta: "In corso", abbandonata: "Abbandonata", persa: "Persa", vinta: "Vinta" };
export const STATI_PREDEFINITI = ["aperta", "vinta"];
// Stile dei collegamenti ben visibili già in uso (Anagrafiche, Utenti, Aziende)
export const LINK = "text-blue-600 hover:underline";
export const STATO_COLORE = {
  aperta: "bg-sky-100 text-sky-800", vinta: "bg-emerald-100 text-emerald-800",
  persa: "bg-rose-100 text-rose-800", abbandonata: "bg-slate-200 text-slate-700",
};
export const LIVELLI = { nessuno: "Nessuno", proprie: "Proprie", proprie_e_libere: "Proprie e libere", tutte: "Tutte" };
export const TIPI_PIPELINE = { vendita: "Vendita", acquisizione: "Acquisizione", autonoma: "Autonoma" };
export const TIPI_FASE = {
  normale: "Normale", chiusura_da_collegare: "Chiusura da collegare",
  chiusura_collegata: "Chiusura collegata", chiusura: "Chiusura",
};

export function useCrmMe() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/crm/me").then((r) => setMe(r.data)).catch(() => setMe({ menu: false, flag: {}, livelli: {} })); }, []);
  return me;
}

export const haFlagCrm = (crmMe, companyId, flag) => !!crmMe?.flag?.[companyId]?.includes(flag);
export const gestioneQualsiasi = (crmMe) => Object.values(crmMe?.flag || {}).some((l) => l.some((f) => f.startsWith("crm_gestisci")));

export const errore = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Operazione non riuscita";
};

export function StatoBadge({ stato, testid }) {
  return <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${STATO_COLORE[stato] || ""}`}
    data-testid={testid}>{STATI_OPP[stato] || "—"}</span>;
}

export function ContatoreContatto({ conteggi, testid }) {
  if (!conteggi) return <span>—</span>;
  const { n, aperte } = conteggi;
  return <span className="text-xs text-slate-500" data-testid={testid}>
    {n} opportunità · {aperte} apert{aperte === 1 ? "a" : "e"}
  </span>;
}

// Barra schede scura, come nelle schede veicolo e pratica
export function BarraSchede({ schede, attiva, onScegli, testid }) {
  return (
    <div className="sticky top-0 z-20 bg-slate-900 rounded-md shadow-sm" data-testid={testid}>
      <div className="flex gap-1 overflow-x-auto" role="tablist">
        {schede.map((s) => (
          <button key={s.key} role="tab" aria-selected={attiva === s.key} onClick={() => onScegli(s.key)}
            className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-[3px] transition-colors ${attiva === s.key
              ? "border-sky-400 bg-slate-700 text-white font-semibold"
              : "border-transparent text-slate-200 font-medium hover:bg-slate-800 hover:text-white"}`}
            data-testid={`${testid}-${s.key}`}>{s.label}</button>
        ))}
      </div>
    </div>
  );
}

// Sprint CRM-1.1 — pulsante «torna» unico per tutte le pagine CRM
export function TornaIndietro({ to, testo, testid, onClick }) {
  return (
    <Button variant="outline" asChild className="border-[#0F4C81] text-[#0F4C81] hover:bg-sky-50" data-testid={testid}>
      <Link to={to} onClick={onClick}><ArrowLeft className="h-4 w-4 mr-1" />{testo}</Link>
    </Button>
  );
}

// Sprint CRM-2A.1 — date e ore italiane
export const fmtDataOra = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit",
  month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(",", " alle") : "—");

export const TIPI_SOGGETTO = { privato: "Privato", azienda: "Azienda" };
export const TipoSoggetto = ({ tipo, testid }) => <span className="text-xs text-slate-500" data-testid={testid}>{TIPI_SOGGETTO[tipo] || "—"}</span>;

// Due pulsanti facoltativi, nessuno preselezionato; un secondo clic toglie la scelta
export function SceltaSoggetto({ value, onChange, prefix }) {
  return (
    <div className="flex gap-1" data-testid={prefix}>
      {Object.entries(TIPI_SOGGETTO).map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(value === k ? "" : k)} data-testid={`${prefix}-${k}`} aria-pressed={value === k}
          className={`px-3 py-1.5 rounded border text-sm transition-colors ${value === k ? "bg-sky-100 border-sky-500 text-sky-900 font-semibold" : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>
      ))}
    </div>
  );
}

// Sprint CRM-2A.1 — pallino del titolare: colore FISSO ricavato dall'id, leggibile con il testo bianco
const TAVOLOZZA = ["#0F4C81", "#B91C1C", "#047857", "#7C3AED", "#C2410C", "#0E7490", "#BE185D", "#4D7C0F", "#1D4ED8", "#92400E"];
export function coloreUtente(id) {
  let h = 0;
  for (const ch of String(id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TAVOLOZZA[h % TAVOLOZZA.length];
}
export const iniziali = (nome) => {
  const p = String(nome || "").trim().split(/\s+/).filter(Boolean);
  return (p.length > 1 ? p[0][0] + p[p.length - 1][0] : (p[0] || "").slice(0, 2)).toUpperCase();
};
export function Pallino({ id, nome, testid }) {
  if (!id) return <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400" title="Senza titolare" data-testid={testid}><User className="h-4 w-4" /></span>;
  return <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" title={nome || ""}
    style={{ backgroundColor: coloreUtente(id) }} data-testid={testid}>{iniziali(nome)}</span>;
}

// Sprint CRM-1.1 — il CRM lavora su UN'azienda alla volta; l'ultima scelta si ricorda nel browser
const LS_AZIENDA = "crm-azienda";
export function useAziendaCrm(crmMe) {
  const [cid, setCid] = useState(() => { try { return localStorage.getItem(LS_AZIENDA) || ""; } catch { return ""; } });
  const aziende = crmMe?.aziende || [];
  const valida = aziende.some((a) => a.id === cid) ? cid : aziende[0]?.id || "";
  const scegli = (id) => { setCid(id); try { localStorage.setItem(LS_AZIENDA, id); } catch {} };
  return [valida, scegli, aziende];
}

// Sprint CRM-2A — per ogni azienda si ricorda l'ultima pipeline; se non è più fra quelle accessibili, la prima
const lsPipeline = (cid) => `crm-pipeline-${cid}`;
export function pipelineScelta(cid, pipelines, richiesta) {
  const ok = (id) => !!id && pipelines.some((p) => p.id === id);
  if (ok(richiesta)) return richiesta;
  let mem = "";
  try { mem = localStorage.getItem(lsPipeline(cid)) || ""; } catch {}
  return ok(mem) ? mem : pipelines[0]?.id || "";
}
export const ricordaPipeline = (cid, pid) => { try { if (cid && pid) localStorage.setItem(lsPipeline(cid), pid); } catch {} };
// Sprint CRM-2A.1 — aprire una pipeline imposta anche la sua azienda
export const apriPipeline = (cid, pid) => { try { localStorage.setItem(LS_AZIENDA, cid); } catch {} ricordaPipeline(cid, pid); };

// Sprint CRM-2A — titolo come la pagina WEB; l'icona è quella della voce CRM del menu laterale
const IconaCrm = VOCI_MENU.find((v) => v.to === "/crm").icon;
export function TitoloCrm({ sotto }) {
  return (
    <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2 shrink-0" data-testid="crm-titolo">
      <IconaCrm className="h-6 w-6 text-blue-600" /> CRM{sotto && <span className="text-slate-400 font-normal">· {sotto}</span>}
    </h1>
  );
}

export const PillLead = ({ n }) => (
  <span className="rounded-full bg-sky-100 text-blue-700 px-3 py-0.5 text-sm font-semibold whitespace-nowrap" data-testid="crm-conteggio">
    {n == null ? "—" : n} lead</span>
);

export const Segnalazione = ({ children, testid }) => (
  <span className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800" data-testid={testid}>{children}</span>
);

// Sprint CRM-5 — piccola etichetta «Azienda» sopra il campo, in tutte le intestazioni del CRM
const ConEtichetta = ({ children }) => (
  <div className="flex flex-col gap-0.5"><span className="text-[11px] text-slate-500" data-testid="crm-etichetta-azienda">Azienda</span>{children}</div>
);
export function CampoAzienda({ aziende, cid, onScegli }) {
  if (!aziende.length) return null;
  if (aziende.length === 1) {
    return <ConEtichetta><div className="h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-800 min-w-[12rem]"
      data-testid="crm-azienda-unica">{aziende[0].nome}</div></ConEtichetta>;
  }
  return (
    <ConEtichetta><Select value={cid} onValueChange={onScegli}>
      <SelectTrigger className="w-56" data-testid="crm-azienda-select"><SelectValue /></SelectTrigger>
      <SelectContent>{aziende.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
    </Select></ConEtichetta>
  );
}

// Sprint CRM-5 — UNA formattazione dei telefoni in vista (il dato salvato resta con +39; tel: e WhatsApp usano il numero completo):
// cellulari italiani «333 123 4567», fissi italiani senza +39 e senza spazi, esteri con il loro prefisso
export function fmtTelefono(n) {
  if (!n) return "—";
  if (!n.startsWith("+39")) return n;
  const r = n.slice(3);
  return /^3\d{8,9}$/.test(r) ? `${r.slice(0, 3)} ${r.slice(3, 6)} ${r.slice(6)}` : r;
}

// Sprint CRM-5 — testo dell'etichetta bianco o scuro secondo la luminosità del colore (il giallo con testo scuro)
export function testoSu(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#ffffff";
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? "#0f172a" : "#ffffff";
}
export const stileEtichetta = (colore) => ({ backgroundColor: colore || "#64748b", color: testoSu(colore || "#64748b") });

// Sprint CRM-9 — ogni cambio di fase fatto da una persona chiede una nota (non vuota); «Annulla» = nessuno spostamento
export const MSG_SPOSTA = "Prendi in carico la lead per spostarla";

// Sprint CRM-10 — «Marca Modello – Targa» dal veicolo agganciato, altrimenti dal descritto; senza dati «—»
export function testoVeicolo(o) {
  const v = (o?.veicolo?.marca || o?.veicolo?.modello || o?.veicolo?.targa) ? o.veicolo : (o?.veicolo_descritto || {});
  const mm = [v.marca, v.modello].filter(Boolean).join(" ");
  if (mm && v.targa) return `${mm} – ${v.targa}`;
  return mm || (v.targa ? `– ${v.targa}` : "—");
}

// Sprint CRM-10 — regola unica: un tocco (o clic) su un comando disattivato mostra la spiegazione vicino al comando per 3 secondi
export function Spiegabile({ motivo, children, testid, className = "" }) {
  const [vedi, setVedi] = useState(false);
  useEffect(() => {
    if (!vedi) return undefined;
    const t = setTimeout(() => setVedi(false), 3000);
    return () => clearTimeout(t);
  }, [vedi]);
  if (!motivo) return children;
  return (
    <span className={`relative inline-flex ${className}`} title={motivo} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setVedi(true); }}>
      <span className="pointer-events-none inline-flex max-w-full">{children}</span>
      {vedi && <span role="status" className="absolute right-0 top-full mt-1 z-30 w-max max-w-[16rem] rounded bg-slate-800 px-2 py-1 text-[11px] text-white shadow"
        data-testid={`${testid}-messaggio`}>{motivo}</span>}
    </span>);
}

// Sprint CRM-10 — campo nota: alto 2 righe, si allunga scrivendo fino a circa 8 righe, poi scorre
// Sprint CRM-10b — `maxRighe` (veicolo descritto: 15); l'altezza segue anche il testo già presente all'apertura
export function CampoNota({ value, onChange, placeholder, testid, maxRighe = 8 }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const riga = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const max = riga * maxRighe + 16;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [value, maxRighe]);
  return <Textarea ref={ref} rows={2} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="w-full min-h-[3.5rem] resize-none" data-testid={testid} />;
}
export function useNotaSpostamento() {
  const [stato, setStato] = useState(null);
  const [testo, setTesto] = useState("");
  const chiedi = (percorso) => new Promise((resolve) => { setTesto(""); setStato({ percorso, resolve }); });
  const chiudi = (v) => { stato?.resolve(v); setStato(null); };
  const finestra = (
    <Dialog open={!!stato} onOpenChange={(v) => { if (!v) chiudi(null); }}>
      <DialogContent className="max-w-md" data-testid="crm-nota-sposta-dialog">
        <DialogHeader><DialogTitle>Perché la sposti?</DialogTitle></DialogHeader>
        {stato?.percorso && <p className="text-sm text-slate-600" data-testid="crm-nota-sposta-percorso">{stato.percorso}</p>}
        <CampoNota value={testo} onChange={setTesto} placeholder="Scrivi una nota" testid="crm-nota-sposta-testo" />
        <DialogFooter>
          <Button variant="outline" onClick={() => chiudi(null)} data-testid="crm-nota-sposta-annulla">Annulla</Button>
          <Button disabled={!testo.trim()} onClick={() => chiudi(testo.trim())} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-nota-sposta-conferma">Sposta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
  return [chiedi, finestra];
}

// Sprint CRM-10b — avviso «Hai modifiche non salvate» (unico, per le schede opportunità e contatto). Intercetta: cambio di
// sezione (prosegui), link e comandi fuori dalla scheda (menu laterale, campanella, ricerca), tasto indietro, chiusura/ricarica.
const FUORI_GUARDIA = "[role='dialog'], [role='alertdialog'], [data-radix-popper-content-wrapper], [role='listbox'], [role='menu'], [data-sonner-toaster]";
export function useModificheNonSalvate(radice) {
  const voci = useRef({});
  const lascia = useRef(false);
  const viaIndietro = useRef(false);
  const [attivo, setAttivo] = useState(false);
  const [azione, setAzione] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const sporco = () => !lascia.current && Object.values(voci.current).some((v) => v.sporco);
  const registra = useCallback((nome, stato) => {
    if (stato) voci.current[nome] = stato; else delete voci.current[nome];
    setAttivo(Object.values(voci.current).some((v) => v.sporco));
  }, []);
  const prosegui = useCallback((fn) => { if (sporco()) setAzione(() => fn); else fn(); }, []);
  // la voce-sentinella si toglie PRIMA di proseguire: dopo, il tasto indietro torna subito alla pagina precedente
  const togliSentinella = () => new Promise((fatto) => {
    if (!window.history.state?.crmGuardia) { fatto(); return; }
    const pop = () => { window.removeEventListener("popstate", pop); fatto(); };
    window.addEventListener("popstate", pop);
    window.history.back();
  });
  const esegui = async (fn, { scarta = false } = {}) => {
    lascia.current = true;
    setAzione(null);
    if (scarta) Object.values(voci.current).forEach((v) => v.sporco && v.scarta?.());  // «Esci senza salvare» scarta davvero
    await togliSentinella();
    fn?.();
    setTimeout(() => { lascia.current = false; }, 0);
  };
  useEffect(() => {
    if (!attivo) return undefined;
    const chiudi = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    const sentinella = () => window.history.pushState({ ...(window.history.state || {}), crmGuardia: true }, "");
    const indietro = () => {
      if (lascia.current || viaIndietro.current) return;
      sentinella();
      setAzione(() => () => { viaIndietro.current = true; window.history.back(); });
    };
    const clic = (e) => {
      if (!sporco()) return;
      const el = e.target.closest?.("a[href], button, [role='button'], input");
      if (!el) return;
      const link = el.matches("a[href]");
      // Sprint CAL-2 — i link passano dall'avviso anche dentro le finestre (lead, contatto, veicolo…); i comandi delle finestre restano liberi
      if (!link && el.closest(FUORI_GUARDIA)) return;
      if (!link && radice.current?.contains(el)) return;  // i comandi della scheda restano liberi
      if (link && (el.target === "_blank" || /^(mailto:|tel:|#)/.test(el.getAttribute("href") || ""))) return;
      e.preventDefault();
      e.stopPropagation();
      setAzione(() => () => (el.matches("input") ? el.focus() : el.click()));
    };
    sentinella();
    window.addEventListener("beforeunload", chiudi);
    window.addEventListener("popstate", indietro);
    document.addEventListener("click", clic, true);
    return () => {
      window.removeEventListener("beforeunload", chiudi);
      window.removeEventListener("popstate", indietro);
      document.removeEventListener("click", clic, true);
      if (window.history.state?.crmGuardia && !viaIndietro.current && !lascia.current) window.history.back();  // salvato restando qui: via la sentinella
    };
  }, [attivo, radice]);  // eslint-disable-line react-hooks/exhaustive-deps
  const salvaEdEsci = async () => {
    setSalvando(true);
    lascia.current = true;  // la sentinella la toglie esegui, non la pulizia dopo il salvataggio
    try {
      for (const v of Object.values(voci.current)) {
        if (v.sporco && !(await v.salva())) { lascia.current = false; setAzione(null); return; }  // errore o conflitto: si resta
      }
      await esegui(azione);
    } finally { setSalvando(false); }
  };
  const finestra = (
    <Dialog open={!!azione} onOpenChange={(v) => { if (!v) setAzione(null); }}>
      <DialogContent className="max-w-md" data-testid="crm-modifiche-dialog">
        <DialogHeader><DialogTitle>Hai modifiche non salvate</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Cosa vuoi fare prima di uscire?</p>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setAzione(null)} data-testid="crm-modifiche-resta">Resta</Button>
          <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => esegui(azione, { scarta: true })} data-testid="crm-modifiche-esci">Esci senza salvare</Button>
          <Button onClick={salvaEdEsci} disabled={salvando} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-modifiche-salva">Salva ed esci</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
  return { registra, prosegui, finestra };
}

// Sprint CRM-10b — una parte della scheda segnala se ha modifiche, come salvarle (salva → true se riuscito) e come scartarle
export function useSegnalaModifiche(registra, nome, sporco, salva, scarta) {
  const s = useRef(salva);
  s.current = salva;
  const d = useRef(scarta);
  d.current = scarta;
  useEffect(() => { registra?.(nome, { sporco, salva: () => s.current(), scarta: () => d.current?.() }); }, [registra, nome, sporco]);
  useEffect(() => () => registra?.(nome, null), [registra, nome]);
}

